<template>
  <!-- Debug info -->
  <div :class="[
    'sidebar',
    {
      'sidebar-collapsed': isCollapsed,
      'sidebar-visible': isSidebarVisible || !isMobile,
      'sidebar-hidden': !isSidebarVisible && isMobile
    }
  ]">
    <div class="sidebar-content">
      <nav class="sidebar-menu">
        <ul class="menu-list">
          <li v-for="(item, index) in items" :key="index" v-show="item.visible">
            <router-link :to="item.path" class="menu-item" active-class="active">
              <i :class="item.icon"></i>
              <span v-show="!isCollapsed" class="menu-label">{{ item.label }}</span>
              <div v-if="isCollapsed" class="tooltip">{{ item.label }}</div>
            </router-link>
          </li>
        </ul>
      </nav>
    </div>
  </div>
  <!-- Overlay только для мобильных устройств -->
  <div
    v-if="isMobile"
    :class="['sidebar-overlay', { 'active': isSidebarVisible }]"
    @click="toggleSidebar"
  ></div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

export default {
  emits: ['toggle-collapse', 'toggle-sidebar'],
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },
    isSidebarVisible: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit }) {
    const router = useRouter();
    const store = useStore();
    const isMobile = ref(window.innerWidth < 992);

    // Get user data from store
    const user = computed(() => store.state.users.user);
    const isLogged = computed(() => store.state.users.user.isLogged);

    const items = computed(() => {
      const menu = [
        {
          label: 'Dashboard',
          icon: 'pi pi-fw pi-chart-bar',
          path: '/dashboard',
          visible: isLogged.value
        },
        {
          label: 'Service Orders',
          icon: 'pi pi-fw pi-wrench',
          path: '/service-orders',
          visible: isLogged.value
        },
        {
          label: 'History',
          icon: 'pi pi-fw pi-history',
          path: '/history',
          visible: isLogged.value
        }
      ];

      return menu;
    });

    const logoSrc = computed(() => {
      return store.state.isTheme === 'dark' ? require('@/assets/ShopInfoboardIcon512.svg') : require('@/assets/ShopInfoboardIcon512.svg');
    });

    const toggleCollapse = () => {
      emit('toggle-collapse');
    };

    const toggleSidebar = () => {
      emit('toggle-sidebar');
    };

    // Resize handler
    const handleResize = () => {
      const wasMobile = isMobile.value;
      isMobile.value = window.innerWidth < 992;

      // Обновляем состояние в store
      store.commit('modals/setSidebarMobile', isMobile.value);
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
      // Инициализируем состояние мобильного режима в store
      store.commit('modals/setSidebarMobile', isMobile.value);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    return {
      items,
      logoSrc,
      isMobile,
      toggleCollapse,
      toggleSidebar
    };
  },
};
</script>

<style>
.sidebar {
  width: 250px;
  position: fixed;
  height: calc(100% - 4rem);
  top: 4rem;
  left: 0;
  z-index: 999;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  will-change: width, transform;
}

.sidebar-collapsed {
  width: 70px;
}

/* Mobile sidebar visibility */
@media (max-width: 991px) {
  .sidebar-hidden {
    transform: translateX(-100%);
    transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .sidebar-visible {
    transform: translateX(0);
    transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  }
}

/* Desktop - always visible */
@media (min-width: 992px) {
  .sidebar {
    transform: translateX(0) !important;
  }
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 998;
  opacity: 0;
  visibility: hidden;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.sidebar-overlay.active {
  opacity: 1;
  visibility: visible;
}

.sidebar-content {
  flex: 1;
  padding: 0.5rem 0;
  overflow: hidden;
  transition: padding 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.sidebar-menu {
  width: 100%;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

/* Обычное меню - без PrimeVue */
.menu-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  color: #495057;
  cursor: pointer;
  transition: padding 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  text-decoration: none;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  will-change: padding;
}

.sidebar-collapsed .menu-item {
  padding: 1rem 0.75rem;
  justify-content: center;
  transition: padding 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}

.menu-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.sidebar-collapsed .menu-item:hover {
  background-color: rgba(74, 222, 128, 0.08);
}

.menu-item.active {
  background-color: rgba(74, 222, 128, 0.15);
  color: #4ade80;
  border-left: 3px solid #4ade80;
  margin-left: -3px;
  padding-left: calc(1.25rem + 3px);
  transform: translateX(0);
}

.sidebar-collapsed .menu-item.active {
  margin-left: 0;
  padding-left: 0.75rem;
  border-left: none;
  border-right: 3px solid #4ade80;
}

.menu-item i {
  font-size: 1.375rem;
  margin-right: 0.75rem;
  min-width: 1.375rem;
  text-align: center;
  transition: none;
  transform: scale(1);
}

.sidebar-collapsed .menu-item i {
  margin-right: 0;
  transform: scale(1.2);
}

.menu-item:hover i {
  color: #4ade80;
}

.sidebar-collapsed .menu-item:hover i {
  color: #4ade80;
}

.menu-label {
  font-size: 1rem;
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 1;
  transform: translateX(0);
  will-change: opacity, transform;
}

.sidebar-collapsed .menu-label {
  opacity: 0;
  transform: translateX(-20px);
  transition-delay: 0s;
}

.sidebar:not(.sidebar-collapsed) .menu-label {
  transition-delay: 0.15s;
}

.tooltip {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%) translateX(-15px) scale(0.7);
  background-color: #333;
  color: white;
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  font-size: 0.875rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.4s cubic-bezier(0.23, 1, 0.32, 1), visibility 0.4s cubic-bezier(0.23, 1, 0.32, 1), transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  z-index: 1000;
  margin-left: 0.5rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.tooltip::before {
  content: '';
  position: absolute;
  top: 50%;
  left: -5px;
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-right-color: #333;
  transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.sidebar-collapsed .menu-item:hover .tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateY(-50%) translateX(8px) scale(1);
  transition-delay: 0.3s;
}

.dark .menu-item {
  color: #e9ecef;
}

.dark .menu-item:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.dark .menu-item.active {
  background-color: rgba(74, 222, 128, 0.15);
  color: #4ade80;
}

.dark .tooltip {
  background-color: #555;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.dark .tooltip::before {
  border-right-color: #555;
}

.sidebar-logo {
  height: 54px;
  padding-top: 4px;
}

/* Dark mode adjustments */
.dark .sidebar {
  background-color: #1e1e1e;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.3);
}

/* Smooth transitions for content layout */
.layout-content-wrapper {
  transition: margin-left 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}
</style>
